// Virtual-reserve bonding curve (pump-style). Constant product on virtual reserves.
// Price = vUsd / vTokens. All functions reject non-finite / non-positive input.

import { TUNABLES } from "@degen/shared/config/tunables";

export interface Curve {
  vUsd: number;
  vTokens: number;
  realUsd: number; // USD actually deposited (vUsd - vUsd0)
  tokensSold: number;
}

export interface SwapResult {
  in: number; // amount actually consumed (may be less than requested at the supply cap)
  out: number;
}

const ZERO: SwapResult = { in: 0, out: 0 };

const valid = (x: number) => Number.isFinite(x) && x > 0;

export function createCurve(): Curve {
  const c = TUNABLES.market.curve;
  return { vUsd: c.vUsd0, vTokens: c.vTokens0, realUsd: 0, tokensSold: 0 };
}

export function curvePrice(c: Curve): number {
  return c.vUsd / c.vTokens;
}

/** Tokens out for `usdIn`, clamped so tokensSold never exceeds the curve supply. */
export function curveQuoteBuy(c: Curve, usdIn: number): SwapResult {
  if (!valid(usdIn)) return ZERO;
  const k = c.vUsd * c.vTokens;
  const remaining = TUNABLES.market.curve.curveSupply - c.tokensSold;
  if (remaining <= 0) return ZERO;
  let out = c.vTokens - k / (c.vUsd + usdIn);
  let used = usdIn;
  if (out > remaining) {
    out = remaining;
    used = k / (c.vTokens - remaining) - c.vUsd;
  }
  if (!valid(out) || !valid(used)) return ZERO;
  return { in: used, out };
}

export function curveBuy(c: Curve, usdIn: number): SwapResult {
  const r = curveQuoteBuy(c, usdIn);
  if (r.out <= 0) return ZERO;
  c.vUsd += r.in;
  c.vTokens -= r.out;
  c.realUsd += r.in;
  c.tokensSold += r.out;
  return r;
}

/** USD out for selling `tokensIn`, clamped to tokens sold and real USD in the curve. */
export function curveQuoteSell(c: Curve, tokensIn: number): SwapResult {
  if (!valid(tokensIn)) return ZERO;
  const tIn = Math.min(tokensIn, c.tokensSold);
  if (tIn <= 0) return ZERO;
  const k = c.vUsd * c.vTokens;
  const out = Math.min(c.vUsd - k / (c.vTokens + tIn), c.realUsd);
  if (!valid(out)) return ZERO;
  return { in: tIn, out };
}

export function curveSell(c: Curve, tokensIn: number): SwapResult {
  const r = curveQuoteSell(c, tokensIn);
  if (r.out <= 0) return ZERO;
  c.vUsd -= r.out;
  c.vTokens += r.in;
  c.realUsd -= r.out;
  c.tokensSold -= r.in;
  if (c.realUsd < 1e-9) c.realUsd = 0;
  if (c.tokensSold < 1e-6) c.tokensSold = 0;
  return r;
}
